<template>
  <div
    class="live-call-wrapper"
    :class="{ 'whiteboard-mode': whiteboardActive }"
  >
    <!-- ===== رأس الصفحة ===== -->
    <header class="call-header">
      <div class="logo">
        <img src="assets/img/acad-logo.png" alt="Logo" />
        <span>أكاديميا جلسة خاصة </span>
      </div>
      <div class="meeting-info">
        <span class="time">{{ currentTime }}</span>
      </div>
      <button @click="activateWhiteboard" class="whiteboard-btn">
        📋 تشغيل الصبورة
      </button>
    </header>

    <!-- ===== منطقة الفيديو والوايتبورد ===== -->
    <div class="main-area">
      <div
        id="whiteboard-container"
        class="whiteboard-area"
        v-show="whiteboardActive"
      ></div>
      <!-- ===== واجهة الاستراحة ===== -->
      <div v-if="isBreakActive" class="break-overlay">
        <i class="fas fa-coffee break-icon"></i>
        <h2 style="font-weight: 900; color: #ddd">📢 الجلسة في استراحة</h2>
      </div>

      <div id="video-container" class="video-grid"></div>
    </div>

    <!-- ===== شريط التحكم ===== -->
    <div class="control-bar">
      <button id="mic-btn" @click="toggleMic" :class="{ active: micEnabled }">
        <i
          :class="micEnabled ? 'fas fa-microphone' : 'fas fa-microphone-slash'"
        ></i>
      </button>
      <button
        id="camera-btn"
        @click="toggleCamera"
        :class="{ active: cameraEnabled }"
      >
        <i :class="cameraEnabled ? 'fas fa-video' : 'fas fa-video-slash'"></i>
      </button>
      <button @click="shareScreen"><i class="fas fa-desktop"></i></button>
      <button @click="leaveSession" class="end-btn">
        <i class="fas fa-phone-slash"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* الوضع العادي */
.main-area {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.video-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.video-item {
  background: #000;
  width: 300px;
  height: 200px;
}

/* عند تفعيل الصبورة */
.whiteboard-mode .main-area {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px); /* يطرح الهيدر + الكنترول بار */
}

.whiteboard-mode .whiteboard-area {
  flex: 1;
  width: 100%;
  background: #fff;
}

.whiteboard-mode .video-grid {
  display: flex;
  gap: 8px;
  padding: 8px;
  overflow-x: auto;
  background: #111;
}

.whiteboard-mode .video-item {
  width: 150px;
  height: 100px;
  flex-shrink: 0;
}
</style>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import AgoraRTC from "agora-rtc-sdk-ng";
import { createFastboard, mount } from "@netless/fastboard";
import { apiUrl } from "@/utils/api";

const micEnabled = ref(true);
const cameraEnabled = ref(true);
const currentTime = ref("");
const whiteboardActive = ref(false);
const isBreakActive = ref(false);

let client;
let localTracks = [];
let fastboardApp = null;
let sessionData = null;

const token = localStorage.getItem("access_token");
const APP_ID = "2e66a5f41b2f4200babc3e87b64efa39"; // Agora RTC
const WHITEBOARD_APP_ID = "3_hwsCTvEfCjNLu8MEujCw/1PfRCRxHsWRN8Appppp"; // Agora Whiteboard (Netless)

function cleanToken(t) {
  if (!t) return t;
  return t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1) : t;
}

// ====== API Calls ======
async function getLiveSessionToken(slotId) {
  try {
    const res = await fetch(`${apiUrl}/live-sessions/get-token`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ slot_id: slotId }),
    });
    return await res.json();
  } catch (err) {
    console.error("Error getting token", err);
    return null;
  }
}

async function getWhiteboardToken(slotId) {
  try {
    const res = await fetch(`${apiUrl}/live-sessions/slot-whiteboard`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ slot_id: slotId }),
    });
    return await res.json();
  } catch (err) {
    console.error("Error getting whiteboard token", err);
    return null;
  }
}

// ====== Clock Update ======
function updateClock() {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ====== Whiteboard Activation (Fastboard) ======
async function activateWhiteboard() {
  if (fastboardApp) {
    whiteboardActive.value = true;
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const slotId = urlParams.get("slot");

  if (!slotId) {
    alert("❌ لا يوجد slot_id");
    return;
  }

  const wbData = await getWhiteboardToken(slotId);
  if (wbData && wbData.whiteboard_token && wbData.whiteboard_room_id) {
    const fastboard = await createFastboard({
      sdkConfig: {
        appIdentifier: WHITEBOARD_APP_ID,
        region: "us-sv",
      },
      joinRoom: {
        uid: String(sessionData.uid),
        uuid: wbData.whiteboard_room_id,
        roomToken: cleanToken(wbData.whiteboard_token),
        writable: false,
      },
      userPayload: {
        isWritable: false,
      },
    });

    mount(fastboard, document.getElementById("whiteboard-container"));

    fastboardApp = fastboard;
    whiteboardActive.value = true;

    const container = document.getElementById("whiteboard-container");
    container.addEventListener("pointerdown", (e) => e.stopPropagation(), true);
    container.addEventListener("pointermove", (e) => e.stopPropagation(), true);
    container.addEventListener("pointerup", (e) => e.stopPropagation(), true);

    await fastboard.room.setWritable(false);

    // ✅ انتظار قصير قبل ضبط الزووم
    setTimeout(async () => {
      try {
        await fastboard.room.moveCameraToContain();
      } catch (err) {
        console.warn("❌ فشل ضبط الزووم:", err);
      }
    }, 200); // 200ms أو يمكن تزيد إذا الصبورة كبيرة
  } else {
    console.warn("❌ فشل في جلب بيانات الوايتبورد");
  }
}

// ====== RTC Join ======
onMounted(async () => {
  setInterval(updateClock, 1000);
  updateClock();

  const urlParams = new URLSearchParams(window.location.search);
  const slotId = urlParams.get("slot");
  const setInstructorBadge = (uid, name, isInstructor) => {
    const player = document.getElementById(`player-${uid}`);
    if (!player) return;

    let badge = document.getElementById(`instructor-badge-${uid}`);
    if (!badge) {
      badge = document.createElement("div");
      badge.id = `instructor-badge-${uid}`;
      badge.style.position = "absolute";
      badge.style.width = "121px";

      badge.style.top = "5px";
      badge.style.right = "0px";
      badge.style.padding = "2px 8px";
      badge.style.borderRadius = "4px";
      badge.style.backgroundColor = "#01c14482";
      badge.style.color = "#fff";
      badge.style.fontSize = "10px";
      badge.style.fontWeight = "bold";
      badge.style.zIndex = "10";
      player.appendChild(badge);
    }
    badge.style.display = isInstructor ? "flex" : "none";
    badge.innerText = isInstructor ? `المحاضر: ${name}` : "";
  };
  if (!slotId) {
    alert("❌ لا يوجد slot_id في الرابط");
    window.close();
    return;
  }

  sessionData = await getLiveSessionToken(slotId);
  if (!sessionData || !sessionData.token) {
    alert("❌ فشل في جلب بيانات الجلسة");
    return;
  }

  client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });

  const instructorUid = Number(atob(urlParams.get("instructor_uid")));
  const instructorName = decodeURIComponent(
    atob(urlParams.get("instructor_name"))
  );

  // 📌 استقبال الرسائل المرسلة عبر الـ Data Stream
  client.on("stream-message", (user, message, streamId) => {
    let decodedMessage = message;
    if (message instanceof Uint8Array) {
      decodedMessage = new TextDecoder().decode(message);
    }
    console.log("📩 رسالة مستلمة:", decodedMessage);
    //alert("📩 رسالة مستلمة: " + decodedMessage);

    // ==== معالجة الأوامر ====
    if (decodedMessage.startsWith("switch_to_video_call")) {
      whiteboardActive.value = false;
    } else if (decodedMessage.startsWith("switch_to_whiteboard")) {
      whiteboardActive.value = true;
      activateWhiteboard();
    } else if (decodedMessage.startsWith("mute_audio:")) {
      const parts = decodedMessage.split(":");
      const muteState = parts[1] === "true";
      const targetId = parts[2] || null;

      if (targetId && targetId == sessionData.uid) {
        const micBtn = document.getElementById("mic-btn");
        micEnabled.value = !muteState;
        localTracks[0]?.setEnabled(!muteState);

        // إخفاء أو إظهار الزر
        micBtn.style.display = muteState ? "none" : "inline-flex";
      }
    } else if (decodedMessage.startsWith("mute_video:")) {
      const parts = decodedMessage.split(":");
      const muteState = parts[1] === "true";
      const targetId = parts[2] || null;

      if (targetId && targetId == sessionData.uid) {
        const cameraBtn = document.getElementById("camera-btn");
        cameraEnabled.value = !muteState;
        localTracks[1]?.setEnabled(!muteState);

        // إخفاء أو إظهار الزر
        cameraBtn.style.display = muteState ? "none" : "inline-flex";
      }
    } else if (decodedMessage.startsWith("grand_whiteboard_permission:")) {
      const userId = decodedMessage.split(":")[1];
      if (userId == sessionData.uid) {
        //activateWhiteboard();
      }
    } else if (decodedMessage.startsWith("take_break:")) {
      const isBreak = decodedMessage.split(":")[1] === "true";
      isBreakActive.value = isBreak;
    } else if (decodedMessage.startsWith("raise_hand:")) {
      // مثال: raise_hand:true:188
      const parts = decodedMessage.split(":");
      const raiseState = parts[1] === "true";
      const targetId = parts[2];

      // ملاحظة: targetId هنا هو uid للمستخدم اللي رفع يده
      // نبحث عن أيقونة اليد الخاصة به
      const handIcon = document.getElementById(`hand-icon-${targetId}`);
      if (handIcon) {
        if (raiseState) {
          // لون نشط (أبيض)
          handIcon.style.color = "#ffffff";
          handIcon.style.opacity = "1";
        } else {
          // لون غير نشط (رمادي شفاف)
          handIcon.style.color = "gray";
          handIcon.style.opacity = "0.4";
        }
      }
    } else if (decodedMessage.startsWith("take_break:")) {
      const isBreak = decodedMessage.split(":")[1] === "true";
      isBreakActive.value = isBreak;

      if (isBreak) {
        alert("📢 تم أخذ استراحة");
      } else {
        alert("🔔 تم إنهاء الاستراحة");
      }
    } else if (decodedMessage === "session_ended") {
      alert("⛔ الجلسة انتهت.");
      // هنا يمكنك إغلاق الصفحة أو إعادة التوجيه
      window.location.href = "/"; // مثال: العودة للصفحة الرئيسية
    }

    // ==== طرد مستخدم ====
    else if (decodedMessage.startsWith("kick_user:")) {
      const targetId = decodedMessage.split(":")[1];
      if (targetId == sessionData.uid) {
        alert("⛔ تم إخراجك من الجلسة.");
        // إنهاء الجلسة للمستخدم المحلي
        window.location.href = "/"; // أو أي صفحة خروج
      } else {
        // إذا أردت: إزالة اللاعب من الواجهة
        const player = document.getElementById(`player-${targetId}`);
        if (player) player.remove();
      }
    }
  });
  const createPlayer = (user) => {
    let player = document.getElementById(`player-${user.uid}`);
    if (!player) {
      player = document.createElement("div");
      player.id = `player-${user.uid}`;
      player.style.display = "flex";
      player.style.flexDirection = "column";
      player.style.alignItems = "center";
      player.style.justifyContent = "center";
      player.style.height = "100%";
      player.style.backgroundColor = "rgb(28, 28, 28)";
      player.style.border = "5px solid #252525";
      player.style.borderRadius = "6px";
      player.style.position = "relative";

      // placeholder المستخدم (كبير)
      const userPlaceholder = document.createElement("div");
      userPlaceholder.style.margin = "10px";
      userPlaceholder.style.width = "120px";
      userPlaceholder.style.height = "120px";
      userPlaceholder.style.borderRadius = "50%";
      userPlaceholder.style.display = "flex";
      userPlaceholder.style.justifyContent = "center";
      userPlaceholder.style.alignItems = "center";
      userPlaceholder.style.backgroundColor = "rgb(27, 32, 30)";
      userPlaceholder.style.border = "3px solid #00562c";
      userPlaceholder.id = `placeholder-${user.uid}`;

      const userIcon = document.createElement("i");
      userIcon.className = "fas fa-user";
      userIcon.style.fontSize = "52px";
      userIcon.style.color = "rgb(112, 112, 112)";
      userPlaceholder.appendChild(userIcon);
      player.appendChild(userPlaceholder);

      // ============== container للأيقونات على اليسار ==============
      const iconContainer = document.createElement("div");
      iconContainer.style.position = "absolute";
      iconContainer.style.left = "5px";
      iconContainer.style.top = "50%";
      iconContainer.style.transform = "translateY(-50%)";
      iconContainer.style.display = "flex";
      iconContainer.style.flexDirection = "column";
      iconContainer.style.gap = "6px";
      iconContainer.style.zIndex = "5";
      iconContainer.style.pointerEvents = "none";
      // أيقونة الصوت
      const audioIcon = document.createElement("i");
      audioIcon.id = `audio-icon-${user.uid}`;
      audioIcon.className = "fas fa-microphone-slash";
      audioIcon.style.fontSize = "18px";
      audioIcon.style.color = "red";
      audioIcon.style.backgroundColor = "rgba(0,0,0,0.6)";
      audioIcon.style.padding = "4px";
      audioIcon.style.borderRadius = "4px";

      // أيقونة الفيديو
      const videoIcon = document.createElement("i");
      videoIcon.id = `video-icon-${user.uid}`;
      videoIcon.className = "fas fa-video";
      videoIcon.style.fontSize = "18px";
      videoIcon.style.color = "red";
      videoIcon.style.backgroundColor = "rgba(0,0,0,0.6)";
      videoIcon.style.padding = "4px";
      videoIcon.style.borderRadius = "4px";

      // أيقونة اليد
      const handIcon = document.createElement("i");
      handIcon.id = `hand-icon-${user.uid}`;
      handIcon.className = "fas fa-hand-paper";
      handIcon.style.fontSize = "18px";
      handIcon.style.color = "gray";
      handIcon.style.backgroundColor = "rgba(0,0,0,0.6)";
      handIcon.style.padding = "4px";
      handIcon.style.borderRadius = "4px";

      iconContainer.appendChild(audioIcon);
      iconContainer.appendChild(videoIcon);
      iconContainer.appendChild(handIcon);
      player.appendChild(iconContainer);
      // =============================================================

      document.getElementById("video-container").appendChild(player);
    }
    return player;
  };

  const updateVideoState = (user, enabled) => {
    const player = document.getElementById(`player-${user.uid}`);
    const videoIcon = document.getElementById(`video-icon-${user.uid}`);
    const placeholder = document.getElementById(`placeholder-${user.uid}`);

    if (enabled) {
      // شغّل الفيديو
      if (user.videoTrack) {
        user.videoTrack.play(player);
        // نعطي للفيديو z-index أقل من الأيقونات
        const videoElement = player.querySelector("video");
        if (videoElement) {
          videoElement.style.position = "absolute";
          videoElement.style.top = "0";
          videoElement.style.left = "0";
          videoElement.style.width = "100%";
          videoElement.style.height = "100%";
          videoElement.style.zIndex = "1";
        }
      }
      // أخضر
      videoIcon.style.color = "green";
      placeholder.style.display = "none";
    } else {
      videoIcon.style.color = "red";
      placeholder.style.display = "flex";
    }
  };

  // المستخدمين الحاليين
  client.remoteUsers.forEach((user) => {
    createPlayer(user);
    updateVideoState(user, !!user.videoTrack);
    if (user.uid === instructorUid) {
      setInstructorBadge(user.uid, instructorName, true);
    }
  });

  client.on("user-joined", (user) => {
    createPlayer(user);
    updateVideoState(user, !!user.videoTrack);
    if (user.uid === instructorUid) {
      setInstructorBadge(user.uid, instructorName, true);
    }
  });

  client.on("user-published", async (user, mediaType) => {
    await client.subscribe(user, mediaType);

    createPlayer(user);
    if (user.uid === instructorUid) {
      setInstructorBadge(user.uid, instructorName, true);
    }

    if (mediaType === "video") updateVideoState(user, true);
    if (mediaType === "audio") {
      user.audioTrack.play();
      const audioIcon = document.getElementById(`audio-icon-${user.uid}`);
      if (audioIcon) audioIcon.style.color = "green";
    }
  });

  client.on("user-unpublished", (user, mediaType) => {
    if (mediaType === "video") updateVideoState(user, false);
    if (mediaType === "audio") {
      const audioIcon = document.getElementById(`audio-icon-${user.uid}`);
      if (audioIcon) audioIcon.style.color = "red";
    }
  });
  try {
    await client.join(
      APP_ID,
      sessionData.channel_name,
      sessionData.token,
      sessionData.uid
    );

    localTracks = await AgoraRTC.createMicrophoneAndCameraTracks();
    const localPlayer = document.createElement("div");
    localPlayer.id = "player-local";
    localPlayer.className = "video-item";
    document.getElementById("video-container").append(localPlayer);
    localTracks[1].play("player-local");
    await client.publish(localTracks);
  } catch (err) {
    console.error("❌ فشل في الانضمام:", err);
    window.close();
  }
});

// ====== Cleanup ======
onBeforeUnmount(async () => {
  await leaveSession();
  if (fastboardApp) {
    fastboardApp.destroy();
  }
});

// ====== Controls ======
async function toggleMic() {
  if (localTracks[0]) {
    micEnabled.value = !micEnabled.value;
    await localTracks[0].setEnabled(micEnabled.value);
  }
}

async function toggleCamera() {
  if (localTracks[1]) {
    cameraEnabled.value = !cameraEnabled.value;
    await localTracks[1].setEnabled(cameraEnabled.value);
  }
}

async function shareScreen() {
  try {
    const screenTrack = await AgoraRTC.createScreenVideoTrack();
    await client.unpublish(localTracks[1]);
    localTracks[1].stop();
    localTracks[1].close();
    localTracks[1] = screenTrack;
    localTracks[1].play("player-local");
    await client.publish([screenTrack]);

    screenTrack.on("track-ended", async () => {
      const [mic, camera] = await AgoraRTC.createMicrophoneAndCameraTracks();
      localTracks[1] = camera;
      camera.play("player-local");
      await client.publish([camera]);
    });
  } catch (err) {
    console.error("Screen share error:", err);
  }
}

async function leaveSession() {
  for (const track of localTracks) {
    track.stop();
    track.close();
  }
  localTracks = [];

  if (client) {
    await client.leave();
  }
  window.close();
}
</script>

<style scoped>
.whiteboard-btn {
  background: #0ac000ff;
  color: white;
  padding: 6px 12px;
  margin-left: 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}
.whiteboard-btn:hover {
  background: #2980b9;
}

.live-call-wrapper {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #1c1c1c;
  color: white;
}

/* ===== رأس الصفحة ===== */
.call-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: #202124;
  border-bottom: 1px solid #333;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
}
.logo img {
  height: 80px;
}
.logo span {
  font-weight: bold;
  font-size: 1.1rem;
}

.meeting-info {
  font-size: 0.9rem;
  opacity: 0.8;
}

/* ===== منطقة الفيديو والوايتبورد ===== */
.main-area {
  flex: 1;
  display: flex;
  gap: 10px;
  padding: 8px;
  background: #000;
}

.video-grid {
  flex: 1.2;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  grid-gap: 8px;
  background: #111;
  padding: 8px;
  border-radius: 8px;
  overflow-y: auto;
}

.video-item {
  position: relative; /* ضروري علشان الأيقونة تتمركز داخله */
  background: #222;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16/9;
}

/* ===== منطقة الوايتبورد ===== */
.whiteboard-area {
  flex: 1;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  min-width: 400px;
}

/* ===== شريط التحكم ===== */
.control-bar {
  background: rgba(0, 0, 0, 0.85);
  padding: 12px;
  display: flex;
  justify-content: center;
  gap: 15px;
  border-top: 1px solid #333;
}

.control-bar button {
  background: #333;
  border: none;
  padding: 20px 24px;
  border-radius: 10px;
  color: white;
  font-size: 1.2rem;
  transition: background 0.2s;
  cursor: pointer;
}

.control-bar button:hover {
  background: #555;
}

.control-bar button.active {
  background: #0f9d58;
}

.end-btn {
  background: #d93025 !important;
}

.end-btn:hover {
  background: #b02118 !important;
}
/* ===== وضع الصبورة fullscreen مع مصغرات الفيديو ===== */
.live-call-wrapper.whiteboard-mode .main-area {
  flex-direction: column;
  padding: 0;
}

.live-call-wrapper.whiteboard-mode .whiteboard-area {
  flex: 1;
  width: 100%;
  height: calc(100vh - 180px); /* يطرح الهيدر + الكنترول بار */
  min-width: unset;
  border-radius: 0;
}

.live-call-wrapper.whiteboard-mode .video-grid {
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  background: #111;
  flex: 0 0 auto;
}

.live-call-wrapper.whiteboard-mode .video-item {
  flex: 0 0 auto;
  width: 150px;
  height: 100px;
  border-radius: 6px;
}

.video-placeholder-icon {
  font-size: 48px; /* حجم الأيقونة */
  color: #888; /* لون الأيقونة */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.break-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(28, 28, 28, 0.95);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10; /* فوق الفيديوهات */
  color: white;
}

.break-icon {
  font-size: 80px;
  margin-bottom: 20px;
  color: #03c546; /* لون مميز */
}
</style>
